import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { BoardApi } from "../../../services/board-service-api";
import { boardKeys } from "./board-query-keys";
import { BoardService } from "./board-service";
import { BoardConfig } from "./types";

export interface BoardConfigState {
  config: BoardConfig;
  loaded: boolean;
}

interface KuikaProjectGlobals {
  activeProjectId?: string;
}
interface KuikaScopedWindow {
  kuika?: KuikaProjectGlobals;
}

// The board is scoped server-side by the active project (the `currentProjectId` request header =
// window.kuika.activeProjectId, falling back to the user's default project). Read it so the config
// cache can be partitioned per scope — the designer overlay edits a specific project, the standalone
// route may have none, and switching between them must not reuse another scope's board list.
export function activeBoardScope(): string {
  return (window as unknown as KuikaScopedWindow).kuika?.activeProjectId ?? "";
}

// Stable identity for the pre-load state so consumers that depend on `config` don't churn.
const EMPTY_CONFIG: BoardConfig = {
  initialized: false,
  projects: [],
  statuses: [],
  boards: [],
  labels: [],
  sprints: [],
  versions: [],
  customFieldDefs: [],
  automations: [],
  canManage: false,
  accessRestricted: false,
  accessRequested: false
};

// The board's bounded lookups (projects/statuses/boards/labels/…), loaded once on demand and
// cached by TanStack Query — shared across every consumer via the "board/config" key. It
// refetches only on config-mutating events (BoardService.subscribeConfig), so routine issue
// edits never re-hit /config; teammates' config changes arriving over SSE invalidate it too.
export function useBoardConfig(): BoardConfigState {
  const queryClient: QueryClient = useQueryClient();
  const scope: string = activeBoardScope();
  const configQuery = useQuery({
    queryKey: boardKeys.config(scope),
    queryFn: (): Promise<BoardConfig> => BoardApi.getConfig()
  });

  useEffect(() => {
    return BoardService.subscribeConfig(() => {
      void queryClient.invalidateQueries({ queryKey: boardKeys.config(scope) });
    });
  }, [queryClient, scope]);

  return { config: configQuery.data ?? EMPTY_CONFIG, loaded: configQuery.isSuccess };
}
